/**
 * ==========================================================================
 * Simple Home - JavaScript Principal
 * Bundle optimizado para la Home de mono-curso
 * Bootstrap se carga desde vendor (externo)
 * Lucide Icons se carga vía CDN (ver home_simple.blade.php)
 * ==========================================================================
 */

// Alpine.js
import Alpine from "alpinejs";
window.Alpine = Alpine;
Alpine.start();

// Swiper - CSS y JS
import "swiper/css/bundle";
import Swiper from "swiper";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";

// Configurar Swiper con módulos
Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

/**
 * Inicialización cuando el DOM está listo
 */
document.addEventListener("DOMContentLoaded", function () {
    // Inicializar Swipers
    initSwipers();

    // Inicializar Scroll suave
    initSmoothScroll();

    // Inicializar Toggle de password
    initPasswordToggle();

    // Inicializar Steps Toggle (si existe)
    initStepsToggle();

    // Inicializar Dropdowns en Offcanvas
    initOffcanvasDropdowns();

    // Inicializar ScrollSpy para estado active del menú
    initScrollSpyActive();

    // Inicializar Timeline Process
    initTimelineProcess();

    // Ocultar loading overlay
    hideLoadingOverlay();
});

/**
 * Inicializa los sliders Swiper
 */
function initSwipers() {
    // Hero Slider con efecto fade (login_home_simple)
    const heroSlider = document.querySelector(".swiper-hero");

    if (heroSlider) {
        new Swiper(heroSlider, {
            loop: true,
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
            speed: 1000,
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
        });
    }

    // Slider principal (reemplazo de owl-carousel)
    const homeSliders = document.querySelectorAll(".swiper-home");

    homeSliders.forEach(function (slider) {
        new Swiper(slider, {
            loop: true,
            spaceBetween: 10,
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            breakpoints: {
                // Mobile
                0: {
                    slidesPerView: 1,
                },
                // Tablet
                768: {
                    slidesPerView: 2,
                },
                // Desktop
                1024: {
                    slidesPerView: 3,
                },
            },
        });
    });
}

/**
 * Scroll suave para enlaces de navegación (header, footer y cualquier anchor interno)
 */
function initSmoothScroll() {
    const navLinks = document.querySelectorAll(
        "a[href^='#scrollspy'], #navbar-scroll ul li a, .header-nav-main ul a, .footer-link[href^='#']",
    );

    navLinks.forEach(function (link) {
        link.addEventListener("click", function (event) {
            const hash = this.getAttribute("href");

            if (hash && hash.startsWith("#") && hash.length > 1) {
                const target = document.querySelector(hash);

                if (target) {
                    event.preventDefault();

                    target.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });

                    // Actualizar hash en URL
                    history.pushState(null, null, hash);
                }
            }
        });
    });
}

/**
 * ScrollSpy para marcar el enlace activo según la sección visible
 */
function initScrollSpyActive() {
    const sections = document.querySelectorAll("[id^='scrollspySection']");
    const navLinks = document.querySelectorAll(
        ".header-nav-main ul a[href^='#scrollspy'], .nav-mobile ul a[href^='#scrollspy']",
    );

    if (sections.length === 0 || navLinks.length === 0) return;

    function updateActiveLink() {
        let currentSection = "";
        const headerOffset = 150;

        // Si estamos cerca del top, marcar el Hero (scrollspySection-0)
        if (window.scrollY < 100) {
            currentSection = "scrollspySection-0";
        } else {
            sections.forEach(function (section) {
                const sectionTop = section.offsetTop - headerOffset;
                const sectionHeight = section.offsetHeight;

                if (
                    window.scrollY >= sectionTop &&
                    window.scrollY < sectionTop + sectionHeight
                ) {
                    currentSection = section.getAttribute("id");
                }
            });
        }

        // Limpiar todos los active (incluyendo los puestos por backend)
        navLinks.forEach(function (link) {
            link.classList.remove("active");
            const href = link.getAttribute("href");
            if (href === "#" + currentSection) {
                link.classList.add("active");
            }
        });
    }

    // Ejecutar al cargar
    updateActiveLink();

    // Ejecutar en scroll con throttle
    let ticking = false;
    window.addEventListener("scroll", function () {
        if (!ticking) {
            window.requestAnimationFrame(function () {
                updateActiveLink();
                ticking = false;
            });
            ticking = true;
        }
    });
}

/**
 * Toggle visibilidad de password
 */
function initPasswordToggle() {
    const toggleBtn = document.getElementById("visiblePassword");
    const passwordInput = document.getElementById("inputPassword");

    if (toggleBtn && passwordInput) {
        toggleBtn.addEventListener("click", function (e) {
            e.preventDefault();

            passwordInput.type = "text";

            // Volver a ocultar después de 3 segundos
            setTimeout(function () {
                passwordInput.type = "password";
            }, 3000);
        });
    }
}

/**
 * Toggle de steps (proceso)
 */
function initStepsToggle() {
    const steps = document.querySelectorAll(
        "#process-step-1, #process-step-2, #process-step-3, #process-step-4",
    );

    steps.forEach(function (step) {
        step.addEventListener("click", function () {
            const allStepNumbers = [1, 2, 3, 4];
            const stepId = this.id;
            const stepNum = stepId.split("-")[2];

            const currentCircle = document.getElementById(
                "process-step-circle-" + stepNum,
            );

            if (
                currentCircle &&
                !currentCircle.classList.contains("step-active")
            ) {
                // Ocultar todos los otros steps
                allStepNumbers.forEach(function (num) {
                    if (num != stepNum) {
                        const desc = document.getElementById(
                            "desc-step-" + num,
                        );
                        const circle = document.getElementById(
                            "process-step-circle-" + num,
                        );

                        if (desc) {
                            desc.style.display = "none";
                        }
                        if (circle) {
                            circle.classList.remove("step-active");
                        }
                    }
                });

                // Mostrar el step seleccionado
                const descSelected = document.getElementById(
                    "desc-step-" + stepNum,
                );
                if (descSelected) {
                    descSelected.style.display = "block";
                }
                currentCircle.classList.add("step-active");
            }
        });
    });
}

/**
 * Ocultar overlay de carga
 */
function hideLoadingOverlay() {
    const overlay = document.querySelector(".loading-overlay");
    const body = document.body;

    if (overlay) {
        setTimeout(function () {
            overlay.classList.add("loaded");
            body.classList.remove("loading-overlay-showing");
        }, 500);
    }
}

/**
 * Maneja los dropdowns dentro del offcanvas
 * Los convierte en comportamiento "accordion" (expandir/colapsar inline)
 */
function initOffcanvasDropdowns() {
    const offcanvas = document.getElementById("mobileMenuOffcanvas");

    if (!offcanvas) return;

    // Obtener todos los elementos .dropdown dentro del offcanvas
    // y buscar el primer link hijo (que actúa como toggle)
    const dropdowns = offcanvas.querySelectorAll(".dropdown");

    dropdowns.forEach(function (dropdown) {
        // El toggle es el primer link directo del dropdown (puede ser nav-link o dropdown-item)
        const toggle = dropdown.querySelector(":scope > a");

        if (!toggle) return;

        toggle.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();

            const dropdownMenu = dropdown.querySelector(".dropdown-menu");

            if (!dropdownMenu) return;

            // Cerrar otros dropdowns abiertos (comportamiento accordion)
            const allOpenDropdowns =
                offcanvas.querySelectorAll(".dropdown.show");
            allOpenDropdowns.forEach(function (openDropdown) {
                if (openDropdown !== dropdown) {
                    openDropdown.classList.remove("show");
                    const openMenu =
                        openDropdown.querySelector(".dropdown-menu");
                    if (openMenu) {
                        openMenu.classList.remove("show");
                    }
                }
            });

            // Toggle del dropdown actual
            dropdown.classList.toggle("show");
            dropdownMenu.classList.toggle("show");
        });
    });

    // Cerrar dropdowns cuando se cierra el offcanvas
    offcanvas.addEventListener("hidden.bs.offcanvas", function () {
        const allOpenDropdowns = offcanvas.querySelectorAll(".dropdown.show");
        allOpenDropdowns.forEach(function (openDropdown) {
            openDropdown.classList.remove("show");
            const openMenu = openDropdown.querySelector(".dropdown-menu");
            if (openMenu) {
                openMenu.classList.remove("show");
            }
        });
    });
}

/**
 * Manejo del formulario de login con Enter
 */
document.addEventListener("keypress", function (e) {
    if (e.key === "Enter") {
        const loginForm = document.querySelector(".login-form");
        if (loginForm && loginForm.contains(e.target)) {
            loginForm.submit();
        }
    }
});

/**
 * Timeline Process - Animación de progreso con hover y auto-avance
 * Soporta timeline horizontal (.timeline-step) y vertical (.timeline-item)
 */
function initTimelineProcess() {
    const timeline = document.getElementById("timelineProcess");
    if (!timeline) return;

    // Detectar tipo de timeline: vertical (.timeline-item) u horizontal (.timeline-step)
    const isVertical = timeline.classList.contains("timeline-vertical");
    const steps = timeline.querySelectorAll(
        isVertical ? ".timeline-item" : ".timeline-step",
    );
    const progress = document.getElementById("timelineProgress");
    const cards = document.querySelectorAll(".timeline-card");
    const totalSteps = steps.length;
    let currentStep = 1;
    let autoAdvanceInterval = null;
    let isPaused = false;

    // Calcular porcentaje de progreso
    function getProgressPercent(step) {
        return ((step - 1) / (totalSteps - 1)) * 100;
    }

    // Activar un paso
    function activateStep(stepNum) {
        currentStep = stepNum;

        // Actualizar steps - solo el activo, no acumulativo
        steps.forEach((s) => {
            const num = parseInt(s.dataset.step);
            s.classList.toggle("active", num === stepNum);
        });

        // Actualizar línea de progreso (solo si existe el elemento)
        if (progress) {
            if (isVertical) {
                // Calcular altura basada en posición del step activo
                const activeStep = timeline.querySelector(
                    `.timeline-item[data-step="${stepNum}"]`,
                );
                if (activeStep) {
                    const timelineRect = timeline.getBoundingClientRect();
                    const stepRect = activeStep.getBoundingClientRect();
                    // Altura hasta el centro del círculo activo (35px = mitad del círculo 70px)
                    const heightToCenter = stepRect.top - timelineRect.top + 35;
                    progress.style.height = heightToCenter + "px";
                }
            } else {
                // Timeline horizontal
                progress.style.width = getProgressPercent(stepNum) + "%";
                progress.style.height = "3px";
            }
        }

        // Actualizar cards (solo desktop con timeline horizontal)
        cards.forEach((card) => {
            const num = parseInt(card.dataset.step);
            card.classList.toggle("active", num === stepNum);
        });
    }

    // Auto-avance cada 5 segundos
    function startAutoAdvance() {
        autoAdvanceInterval = setInterval(() => {
            if (!isPaused) {
                currentStep = currentStep >= totalSteps ? 1 : currentStep + 1;
                activateStep(currentStep);
            }
        }, 5000);
    }

    // Pausar auto-avance
    function pauseAutoAdvance() {
        isPaused = true;
    }

    // Reanudar auto-avance
    function resumeAutoAdvance() {
        isPaused = false;
    }

    // Event listeners para hover
    steps.forEach((step) => {
        step.addEventListener("mouseenter", () => {
            pauseAutoAdvance();
            const stepNum = parseInt(step.dataset.step);
            activateStep(stepNum);
        });

        step.addEventListener("mouseleave", () => {
            resumeAutoAdvance();
        });

        // También soportar click para mobile
        step.addEventListener("click", () => {
            const stepNum = parseInt(step.dataset.step);
            activateStep(stepNum);
        });
    });

    // Pausar cuando el mouse está sobre el contenido
    cards.forEach((card) => {
        card.addEventListener("mouseenter", pauseAutoAdvance);
        card.addEventListener("mouseleave", resumeAutoAdvance);
    });

    // Inicializar
    activateStep(1);
    startAutoAdvance();

    // Recalcular en resize
    window.addEventListener("resize", () => {
        activateStep(currentStep);
    });
}

// Exportar para uso global si es necesario
window.SimpleHome = {
    initSwipers,
    initSmoothScroll,
    initOffcanvasDropdowns,
    initTimelineProcess,
};
